import React, { useEffect, useId, useRef, useState } from 'react';

export interface SourceFilterValue {
  auditplus: boolean;
  ocr: boolean;
}

interface SourceFilterProps {
  value: SourceFilterValue;
  onChange: (next: SourceFilterValue) => void;
}

export const DEFAULT_SOURCE_FILTER: SourceFilterValue = {
  auditplus: true,
  ocr: true,
};

export function matchesSourceFilter(isAuditplus: boolean | undefined, filter: SourceFilterValue) {
  if (isAuditplus) return filter.auditplus;
  return filter.ocr;
}

type SourceMode = 'all' | 'auditplus' | 'ocr';

function modeFromValue(value: SourceFilterValue): SourceMode {
  if (value.auditplus && !value.ocr) return 'auditplus';
  if (!value.auditplus && value.ocr) return 'ocr';
  return 'all';
}

function valueFromMode(mode: SourceMode): SourceFilterValue {
  if (mode === 'auditplus') return { auditplus: true, ocr: false };
  if (mode === 'ocr') return { auditplus: false, ocr: true };
  return { auditplus: true, ocr: true };
}

export default function SourceFilter({ value, onChange }: SourceFilterProps) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const radioName = useId();
  const mode = modeFromValue(value);
  const isDefault = mode === 'all';

  const label =
    mode === 'auditplus' ? 'Source: Audit+'
    : mode === 'ocr' ? 'Source: OCR'
    : 'Source: All';

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const options: { mode: SourceMode; text: string }[] = [
    { mode: 'all', text: 'All' },
    { mode: 'auditplus', text: 'Audit+' },
    { mode: 'ocr', text: 'OCR' },
  ];

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`f-btn ${isDefault ? 'f-btn-outline' : 'f-btn-primary'}`}
        aria-expanded={open}
        aria-label="Filter by source"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h18M6 10h12M10 16h4" />
        </svg>
        <span>{label}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 w-52 rounded-lg border border-gray-200 bg-white shadow-lg p-3 space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Source
          </div>
          {options.map((option) => (
            <label
              key={option.mode}
              className="flex items-center gap-2 px-1 py-1.5 text-sm text-gray-800 cursor-pointer rounded hover:bg-gray-50"
            >
              <input
                type="radio"
                name={radioName}
                checked={mode === option.mode}
                onChange={() => onChange(valueFromMode(option.mode))}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
